import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { MoreHorizontal, Repeat } from 'lucide-react';
import type { KanbanCardData, KanbanLabel, BoardMember } from 'types/kanban';
import { formatIntuitiveDueAt, getDueDateIndicator } from '../kanbanDueDate';
import { formatRecurrenceLabel } from '../kanbanRecurrence';
import { getMemberColor } from '../kanbanUtils';
import { isCurrentUser, useCurrentUserAvatar } from '../useCurrentUserAvatar';
import styles from './KanbanCardItem.module.scss';

export interface KanbanCardItemProps {
  card: KanbanCardData;
  isOverlay?: boolean;
  onCardClick: (cardId: string) => void;
  onOpenCardMenu: (
    cardId: string,
    currentColumnId: string,
    e: React.MouseEvent,
  ) => void;
  currentColumnId: string;
  boardLabels?: KanbanLabel[];
  boardMembers?: BoardMember[];
  isEditingTitle?: boolean;
  editingTitleValue?: string;
  onTitleChange?: (value: string) => void;
  onTitleSubmit?: () => void;
  onTitleCancel?: () => void;
}

function KanbanCardItem({
  card,
  isOverlay,
  onCardClick,
  onOpenCardMenu,
  currentColumnId,
  boardLabels = [],
  boardMembers = [],
  isEditingTitle = false,
  editingTitleValue = '',
  onTitleChange,
  onTitleSubmit,
  onTitleCancel,
}: KanbanCardItemProps) {
  const [nowTick, setNowTick] = useState(() => Date.now());
  const { currentUserIds, profilePictureUrl } = useCurrentUserAvatar();

  useEffect(() => {
    if (!card.dueAt) return undefined;
    const id = window.setInterval(() => setNowTick(Date.now()), 10000);
    return () => window.clearInterval(id);
  }, [card.dueAt]);

  const dueIndicator =
    card.dueAt != null && card.dueAt !== ''
      ? getDueDateIndicator(
          new Date(nowTick),
          card.dueAt,
          card.reminderLeadValue,
          card.reminderLeadUnit,
        )
      : null;
  const duePreviewText =
    card.dueAt != null && card.dueAt !== ''
      ? formatIntuitiveDueAt(card.dueAt, new Date(nowTick))
      : '';
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useSortable({
      id: card.id,
      data: { type: 'card', card },
    });

  const titleInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (isEditingTitle) titleInputRef.current?.focus();
  }, [isEditingTitle]);

  const style = transform
    ? !isOverlay
      ? { transform: CSS.Transform.toString(transform) }
      : undefined
    : undefined;

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (isOverlay || isEditingTitle) return;
      e.stopPropagation();
      onCardClick(card.id);
    },
    [card.id, isOverlay, isEditingTitle, onCardClick],
  );

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${styles.card} ${isDragging && !isOverlay ? styles.cardDragging : ''} ${isOverlay ? styles.dragOverlay : ''}`}
      {...(!isOverlay ? { ...attributes, ...listeners } : {})}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !isOverlay) onCardClick(card.id);
      }}
      role="button"
      tabIndex={isOverlay ? undefined : 0}
      aria-label={`Card: ${card.title}`}
    >
      <div className={styles.cardContent}>
        {isEditingTitle ? (
          <input
            ref={titleInputRef}
            type="text"
            className={styles.cardTitleInput}
            value={editingTitleValue}
            onChange={(e) => onTitleChange?.(e.target.value)}
            onBlur={onTitleSubmit}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') onTitleSubmit?.();
              if (e.key === 'Escape') onTitleCancel?.();
            }}
            onClick={(e) => e.stopPropagation()}
            aria-label="Card title"
          />
        ) : (
          <p className={styles.cardTitle}>{card.title}</p>
        )}
        {duePreviewText ? (
          <div
            className={`${styles.dueIndicator} ${
              dueIndicator?.variant === 'overdue'
                ? styles.dueIndicatorOverdue
                : dueIndicator?.variant === 'soon'
                  ? styles.dueIndicatorSoon
                  : styles.dueIndicatorUpcoming
            }`}
          >
            {duePreviewText}
          </div>
        ) : null}
        {card.recurrence ? (
          <div className={styles.recurrenceChip} title="Repeating task">
            <Repeat
              size={11}
              aria-hidden
              className={styles.recurrenceChipIcon}
            />
            <span>{formatRecurrenceLabel(card.recurrence)}</span>
          </div>
        ) : null}
        {(card.labelIds?.length ?? 0) > 0 && boardLabels.length > 0 && (
          <div className={styles.cardLabelsRow}>
            {(card.labelIds ?? [])
              .map((id) => boardLabels.find((l) => l.id === id))
              .filter((l): l is KanbanLabel => Boolean(l))
              .slice(0, 3)
              .map((label) => (
                <span
                  key={label.id}
                  className={styles.cardLabelChip}
                  style={{ background: label.color }}
                  title={label.name}
                >
                  {label.name}
                </span>
              ))}
          </div>
        )}
        {(card.memberIds?.length ?? 0) > 0 && boardMembers.length > 0 && (
          <div className={styles.cardMembersRow}>
            {(card.memberIds ?? [])
              .map((id) => boardMembers.find((m) => m.id === id))
              .filter((m): m is BoardMember => Boolean(m))
              .slice(0, 4)
              .map((member) => (
                <span key={member.id} title={member.name}>
                  {member.profilePictureUrl ? (
                    <img
                      className={styles.cardMemberAvatarImg}
                      src={member.profilePictureUrl}
                      alt=""
                      aria-hidden
                    />
                  ) : profilePictureUrl &&
                    isCurrentUser(
                      currentUserIds,
                      member.userId ? member.userId : member.id,
                    ) ? (
                    <img
                      className={styles.cardMemberAvatarImg}
                      src={profilePictureUrl}
                      alt=""
                      aria-hidden
                    />
                  ) : (
                    <span
                      className={styles.cardMemberAvatar}
                      style={{
                        background: getMemberColor(member.id),
                        color: 'white',
                      }}
                    >
                      {member.name
                        .split(/\s+/)
                        .map((s) => s[0])
                        .join('')
                        .toUpperCase()
                        .slice(0, 2) || '?'}
                    </span>
                  )}
                </span>
              ))}
            {(card.memberIds?.length ?? 0) > 4 && (
              <span className={styles.cardMemberMore}>
                +{(card.memberIds?.length ?? 0) - 4}
              </span>
            )}
          </div>
        )}
      </div>
      {!isOverlay && (
        <div className={styles.cardMenuWrap}>
          <button
            type="button"
            className={styles.menuTriggerBtn}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onOpenCardMenu(card.id, currentColumnId, e);
            }}
            aria-label={`Menu for card ${card.title}`}
          >
            <MoreHorizontal size={16} />
          </button>
        </div>
      )}
    </div>
  );
}

export default memo(KanbanCardItem);
